import { useMotionValueEvent, useScroll } from "framer-motion";
import { Fragment, useCallback, useEffect, useState } from "react";
import { debounce } from "underscore";
import { useShallow } from "zustand/react/shallow";
import { HEADER_HEIGHT, useScrollContext } from "./scrollContext";
import { useScrollDisabled } from "./scrollDisabledContext";
import { scrollRef } from "./scrollSignals";

function useDeferredScroll() {
  const container = scrollRef;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (container?.current) {
      setReady(true);
    }
  }, [container]);

  return useScroll(ready ? { container } : undefined);
}

interface ScrollWrapperProps {
  children: React.ReactNode;
}

export function ScrollWrapper({ children }: ScrollWrapperProps) {
  const { scrollY, scrollYProgress } = useDeferredScroll();

  const { atTop, atBottom } = useScrollContext(
    useShallow((s) => ({
      atTop: s.atTop,
      atBottom: s.atBottom,
    })),
  );
  const { disabled } = useScrollDisabled();
  const DEBOUNCE_TIME = 10;

  const handleScrollYChange = useCallback(() => {
    return debounce((latest: number) => {
      const SCROLL_THRESHOLD = HEADER_HEIGHT;
      const latTop = latest <= SCROLL_THRESHOLD;

      if (atTop !== latTop) {
        useScrollContext.getState().setAtTop(latTop);
      }
    }, DEBOUNCE_TIME);
  }, [atTop]);

  const handleProgressYChange = useCallback(() => {
    return debounce((latest: number) => {
      if (latest > 0.8 && !atBottom) {
        useScrollContext.getState().setAtBottom(true);
      } else if (latest < 0.8 && atBottom) {
        useScrollContext.getState().setAtBottom(false);
      }
    }, DEBOUNCE_TIME);
  }, [atBottom]);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (disabled) return;
    handleProgressYChange()(latest);
  });

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (disabled) return;
    handleScrollYChange()(latest);
  });

  /// reRender elem stored for rerender issue on android. forces rerender of this elem.
  return (
    <Fragment>
      {children}
      <div
        id="reRender"
        style={{
          position: "absolute",
          visibility: "hidden",
          opacity: 0,
          height: "1px",
          width: "1px",
          backgroundColor: "black",
          top: "0",
          right: "0",
          zIndex: 99999999,
        }}
      />
    </Fragment>
  );
}
